import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { LocalizedApiError } from '@/features/audit-issues/LocalizedApiError';

import type { DispatchRequestDto } from './types';

interface ReturnToDraftDispatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  request: DispatchRequestDto | null;
  isPending: boolean;
  error?: unknown;
  onConfirm: (reason: string) => void;
}

/**
 * Platform-management action that moves a submitted/routed/rejected/cancelled
 * dispatch request back to supplier-editable draft without deleting the
 * original request or its audit history.
 */
export const ReturnToDraftDispatchDialog = ({
  open,
  onOpenChange,
  request,
  isPending,
  error,
  onConfirm,
}: ReturnToDraftDispatchDialogProps) => {
  const { t } = useTranslation('operations');
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setReasonError(null);
    }
  }, [open]);

  const submit = () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setReasonError(
        t('efs.operations.dispatch.return_to_draft_dialog.reason_required', {
          defaultValue: '下書きに戻す理由を入力してください。',
        }),
      );
      reasonRef.current?.focus();
      return;
    }
    setReasonError(null);
    onConfirm(trimmed);
  };

  return (
    <Dialog open={open} onOpenChange={isPending ? undefined : onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('efs.operations.dispatch.return_to_draft_dialog.title', {
              defaultValue: '配車依頼を下書きに戻す',
            })}
          </DialogTitle>
          <DialogDescription>
            {t('efs.operations.dispatch.return_to_draft_dialog.subtitle', {
              defaultValue:
                '元の申請と履歴は保持され、下書きに戻した後は供給元が編集して再送信できます。',
            })}
          </DialogDescription>
        </DialogHeader>

        {request ? (
          <p className="text-sm text-efs-text-muted">
            {request.requested_date ?? '—'}
            {' · '}
            <span className="font-mono text-xs">{request.trip_code || '—'}</span>
          </p>
        ) : null}

        <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-efs-text">
          {t('efs.operations.dispatch.return_to_draft_dialog.impact', {
            defaultValue:
              '処理拠点・月・車次の割当、入力済み重量、結果写真は解除されます。元の依頼内容と履歴は残ります。',
          })}
        </p>

        <div className="space-y-2">
          <Label htmlFor="dispatch-return-to-draft-reason">
            {t('efs.operations.dispatch.return_to_draft_dialog.reason_label', {
              defaultValue: '下書きに戻す理由（必須）',
            })}
          </Label>
          <Textarea
            ref={reasonRef}
            id="dispatch-return-to-draft-reason"
            rows={3}
            maxLength={2000}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (e.target.value.trim()) setReasonError(null);
            }}
            placeholder={t('efs.operations.dispatch.return_to_draft_dialog.reason_placeholder', {
              defaultValue: 'ここに理由を入力してください',
            })}
            aria-invalid={!!reasonError}
            aria-describedby={reasonError ? 'dispatch-return-to-draft-reason-error' : undefined}
            autoFocus
          />
          {reasonError ? (
            <p
              id="dispatch-return-to-draft-reason-error"
              role="alert"
              className="text-sm text-red-900"
            >
              {reasonError}
            </p>
          ) : (
            <p className="text-xs text-efs-text-muted">
              {t('efs.operations.dispatch.return_to_draft_dialog.reason_help', {
                defaultValue: 'この理由は履歴に残ります。供給元は内容を修正して再送信できます。',
              })}
            </p>
          )}
        </div>

        <LocalizedApiError error={error} showTechnicalDetails={false} />

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={isPending}
            onClick={() => onOpenChange(false)}
          >
            {t('efs.common.actions.cancel', { ns: 'common' })}
          </Button>
          <Button type="button" variant="default" disabled={isPending} onClick={submit}>
            {isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            {t('efs.operations.dispatch.return_to_draft_dialog.confirm', {
              defaultValue: '下書きに戻す',
            })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
